// Library methods

// Utils
import { ERROR_MESSAGES } from 'utils/constants/toastMessages';
import moment from 'moment';
import { takeLatest, put, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import { POST } from '../../../utils/fetchWrappers';

// API URLs
import {
    API_RETRIGGER_SETTLEMENT,
    API_RETRY_SETTLEMENT,
    API_SETTLEMENT_LISTING,
    API_FETCH_AVAILABLE_BALANCE,
    API_FETCH_ODS_FEE_DETAILS,
    API_PROCESS_ODS,
    API_FETCH_NET_SETTLEMENT,
} from '../../../utils/API/apiUrls';

// API Status Codes
import { apiStatusCodes } from '../../../utils/API/apiStatusCodes';
import * as loaderActions from '../../../redux/common/loader/loader.actions';

// Redux Actions

// Constants
import {
    FETCH_SETTLEMENT_METRICS_REQUEST,
    FETCH_SETTLEMENT_REQUEST,
    RETRIGGER_SETTLEMENT_REQUEST,
    RETRY_SETTLEMENT_REQUEST,
    FETCH_AVAILABLE_BALANCE_REQUEST,
    FETCH_ODS_FEE_DETAILS_REQUEST,
    PROCESS_ODS_REQUEST,
    GET_MERCHANT_SECTION_INFO_REQUEST,
    FETCH_NET_SETTLEMENT_REQUEST,
} from './settlement.constants';
import * as settlementActions from './settlement.actions';

const getRequestData = payload => {
    const request = {
        pageNo: payload.pageNo || 1,
        pageSize: payload.pageSize || 10,
    };
    if (payload.startDate) {
        request.startDateTime = moment(payload.startDate)
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
        request.endDateTime = moment(payload.endDate)
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
    }
    if (payload.settlementMetric) {
        request.settlementMetric = payload.settlementMetric;
    }
    if (payload.entityIds && payload.entityIds.length) {
        request.entityIds = payload.entityIds;
    }
    if (payload.orderId) {
        request.orderId = payload.orderId;
    }
    if (payload.settlementMode) {
        request.settlementMode = payload.settlementMode;
    }
    if (payload.settlementStates) {
        request.settlementStates = payload.settlementStates;
    }

    if (payload.searchValue) {
        request.searchValue = payload.searchValue;
    }

    if (payload.searchKey) {
        request.searchKey = payload.searchKey;
    }
    if (payload.minAmt) {
        request.minAmt = payload.minAmt;
    }
    if (payload.maxAmt) {
        request.maxAmt = payload.maxAmt;
    }
    return request;
};

export function* fetchSettlementDetails(actions) {
    yield put(settlementActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(
            POST,
            API_SETTLEMENT_LISTING,
            getRequestData(payload)
        );

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.fetchSettlementSuccess(data));
        } else if (data && data.errorMessage) {
            if (!payload.settlementMetric) {
                yield put(
                    settlementActions.fetchSettlementError({
                        error: data.errorMessage,
                    })
                );
            }
        }
    } catch ({ response }) {
        // const { data } = response;
        // yield put(settlementActions.fetchSettlementError({ error: data }));
    }
    yield put(settlementActions.hideLoader());
}

export function* fetchSettlementMetrics(actions) {
    try {
        const { payload } = actions;
        const { data } = yield call(
            POST,
            API_SETTLEMENT_LISTING,
            getRequestData(payload)
        );

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.fetchSettlementMetricsSuccess(data));
        } else if (data && data.errorMessage) {
            if (!payload.settlementMetric) {
                yield put(
                    settlementActions.fetchSettlementMetricsError({
                        error: data.errorMessage,
                    })
                );
            }
        }
    } catch ({ response }) {
        // const { data } = response;
        // yield put(
        //     settlementActions.fetchSettlementMetricsError({ error: data })
        // );
    }
}

export function* retrySettlementRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_RETRY_SETTLEMENT, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.retrySettlementSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                settlementActions.retrySettlementError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(settlementActions.retrySettlementError({ error: data }));
    }
    yield put(loaderActions.hideLoader());
}

export function* retriggerSettlementRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_RETRIGGER_SETTLEMENT, payload);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.retriggerSettlementSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                settlementActions.retriggerSettlementError({
                    error: data.errorMessage,
                })
            );
            toast.error(
                data.errorMessage
                    ? data.errorMessage
                    : ERROR_MESSAGES.FALLBACK_ERROR
            );
        }
    } catch ({ response }) {
        const { data } = response;

        yield put(settlementActions.retriggerSettlementError({ error: data }));
        toast.error(
            data.errorMessage
                ? data.errorMessage
                : ERROR_MESSAGES.FALLBACK_ERROR
        );
    }
    yield put(loaderActions.hideLoader());
}

export function* fetchAvailableBalanceRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_FETCH_AVAILABLE_BALANCE, payload);

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.fetchAvailableBalanceSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                settlementActions.fetchAvailableBalanceError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            settlementActions.fetchAvailableBalanceError({ error: data })
        );
    } finally {
        yield put(loaderActions.hideLoader());
    }
}

export function* fetchNetSettlementRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const { data } = yield call(POST, API_FETCH_NET_SETTLEMENT, payload);

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.fetchNetSettlementSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                settlementActions.fetchNetSettlementError({
                    error: data.errorMessage,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(
            settlementActions.fetchAvailableBalanceError({ error: data })
        );
    } finally {
        yield put(loaderActions.hideLoader());
    }
}

export function* fetchOdsDetailsRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload, handleSuccess } = actions;
        const { data } = yield call(POST, API_FETCH_ODS_FEE_DETAILS, payload);

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.fetchOdsDetailsSuccess(data));
            handleSuccess();
        } else if (data && data.errorMessage) {
            yield put(
                settlementActions.fetchOdsDetailsError({
                    error: data.errorMessage,
                })
            );
            toast.error(
                data.errorMessage
                    ? data.errorMessage
                    : ERROR_MESSAGES.FALLBACK_ERROR
            );
        }
    } catch ({ response }) {
        const { data } = response;

        yield put(settlementActions.fetchOdsDetailsError({ error: data }));
        toast.error(
            data.errorMessage
                ? data.errorMessage
                : ERROR_MESSAGES.FALLBACK_ERROR
        );
    } finally {
        yield put(loaderActions.hideLoader());
    }
}

export function* fetchProcessOdsRequest(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload, handleSuccess } = actions;

        const { data } = yield call(POST, API_PROCESS_ODS, payload);

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(settlementActions.fetchProcessOdsSuccess(data));
            handleSuccess();
        } else if (data && data.errorMessage) {
            yield put(
                settlementActions.fetchProcessOdsError({
                    error: data.errorMessage,
                })
            );
            toast.error(
                data.errorMessage
                    ? data.errorMessage
                    : ERROR_MESSAGES.FALLBACK_ERROR
            );
        }
    } catch ({ response }) {
        const { data } = response;
        yield put(settlementActions.fetchProcessOdsError({ error: data }));
        toast.error(
            data.errorMessage
                ? data.errorMessage
                : ERROR_MESSAGES.FALLBACK_ERROR
        );
    } finally {
        yield put(loaderActions.hideLoader());
    }
}
/**
 * WATCHER SAGA
 */
export function* settlementSaga() {
    yield takeLatest(FETCH_SETTLEMENT_REQUEST, fetchSettlementDetails);
    yield takeLatest(FETCH_SETTLEMENT_METRICS_REQUEST, fetchSettlementMetrics);
    yield takeLatest(RETRY_SETTLEMENT_REQUEST, retrySettlementRequest);
    yield takeLatest(RETRIGGER_SETTLEMENT_REQUEST, retriggerSettlementRequest);
    yield takeLatest(
        FETCH_AVAILABLE_BALANCE_REQUEST,
        fetchAvailableBalanceRequest
    );
    yield takeLatest(FETCH_NET_SETTLEMENT_REQUEST, fetchNetSettlementRequest);
    yield takeLatest(FETCH_ODS_FEE_DETAILS_REQUEST, fetchOdsDetailsRequest);
    yield takeLatest(PROCESS_ODS_REQUEST, fetchProcessOdsRequest);
}
